import {
  CompressedRuleDefinitionResponseEnum,
  NewRuleDefinitionResponse,
  RuleIncExc
} from 'app/graphql/generated/graphqlApolloTypes';

import { RuleChangeCause, RuleChangeCauses, SimpleUpsertRule } from 'app/models';

import { Compressed } from './helpers/CompressionUtil';

export type RuleForUndo = {
  rule: SimpleUpsertRule;
  definition: NewRuleDefinitionResponse;
  compressedDefinition: Compressed<CompressedRuleDefinitionResponseEnum.NewRuleDefinitionResponse>;
};

export class RuleUndoStack {
  private static readonly MaxStackSize = 3;

  private lastSeenRules: ReadonlyArray<RuleForUndo> = [];
  private snapshotStack: ReadonlyArray<RuleForUndo>[] = [];
  private waitingForUndoToFinish = false;

  public onRuleChange(cause: RuleChangeCause, rules: ReadonlyArray<RuleForUndo>) {
    switch (cause) {
      case RuleChangeCauses.LimitationChange:
      case RuleChangeCauses.MeasureChange:
        break; // No Impact on stack

      case RuleChangeCauses.Recolor:
      case RuleChangeCauses.FixConflict:
      case RuleChangeCauses.Unassign:
      case RuleChangeCauses.Reassign:
        this.pushToStack(rules);
        break;

      case RuleChangeCauses.FinishUndo:
        this.onUndoFinished(rules);
        break;

      case RuleChangeCauses.FailedUndo:
      case RuleChangeCauses.InitialLoad:
      case RuleChangeCauses.Delete:
      case RuleChangeCauses.Create:
        this.resetStack(rules);
        break;

      default:
        console.warn(`Unhandled RuleChangeCause: ${cause}`);
        this.resetStack(rules);
        break;
    }
  }

  public resetStack(newRules: ReadonlyArray<RuleForUndo>) {
    this.snapshotStack = [];
    this.waitingForUndoToFinish = false;
    this.lastSeenRules = newRules;
  }

  private onUndoFinished(rules: ReadonlyArray<RuleForUndo>) {
    if (!this.waitingForUndoToFinish) {
      console.warn('Encountered undo-finish while not waiting for one, resetting undo stack');
      this.resetStack(rules);
      return;
    }
    this.waitingForUndoToFinish = false;
    this.lastSeenRules = rules;
  }

  private pushToStack(newRules: ReadonlyArray<RuleForUndo>) {
    if (this.waitingForUndoToFinish) {
      console.warn('Encountered unexpected change while waiting for undo to finish, resetting undo stack');
      this.resetStack(newRules);
      return;
    }

    const newRuleMap = new Map(newRules.map((r) => [r.rule.ruleId, r]));
    if (
      newRuleMap.size !== this.lastSeenRules.length ||
      this.lastSeenRules.some((r) => !newRuleMap.has(r.rule.ruleId))
    ) {
      console.warn('Encountered unexpected change in rule IDs, resetting undo stack');
      this.resetStack(newRules);
      return;
    }
    const changedRules = this.lastSeenRules.filter((r) => !areRulesForUndoEqual(r, newRuleMap.get(r.rule.ruleId)));
    if (changedRules.length === 0) {
      console.warn('Unable to tell what changed, resetting undo stack');
      this.resetStack(newRules);
      return;
    }

    this.snapshotStack.push(changedRules);

    if (this.snapshotStack.length > RuleUndoStack.MaxStackSize) {
      this.snapshotStack.shift();
    }
    this.lastSeenRules = newRules;
  }

  public isUndoAvailable() {
    return this.snapshotStack.length > 0;
  }

  public pop() {
    if (this.waitingForUndoToFinish) throw new Error(`Tried to undo before last undo finished`);
    if (!this.isUndoAvailable()) throw new Error(`Tried to undo when unavailable`);
    this.waitingForUndoToFinish = true;
    return this.snapshotStack.pop();
  }

  public isWaitingForUndoToFinish() {
    return this.waitingForUndoToFinish;
  }

  public getCount() {
    return this.snapshotStack.length;
  }
}

export const areRulesForUndoEqual = (a: RuleForUndo, b: RuleForUndo) => {
  // We do not diff:
  // [territoryId, territoryName, territoryGroupId, effectiveDate, endDate]
  // as they cannot be edited on the map
  if (a.rule.color !== b.rule.color) return false;

  if (a.compressedDefinition.compressed === b.compressedDefinition.compressed) {
    // Compressed definitions exactly matching implies full definitions exactly match
    return true;
  }
  // Else we have to slowly check the full definitions
  return areRuleDefinitionsEqual(a.definition, b.definition);
};

const areRuleDefinitionsEqual = (a: NewRuleDefinitionResponse, b: NewRuleDefinitionResponse): boolean => {
  if (a.modifiers.inclusions.length !== b.modifiers.inclusions.length) return false;
  if (a.base.inclusions.length !== b.base.inclusions.length) return false;
  if (a.base.exclusions.length !== b.base.exclusions.length) return false;

  return (
    areRuleIncExcsContainsEqual(a.modifiers.inclusions, b.modifiers.inclusions) &&
    areRuleIncExcsContainsEqual(a.base.inclusions, b.base.inclusions) &&
    areRuleIncExcsContainsEqual(a.base.exclusions, b.base.exclusions)
  );
};

const areRuleIncExcsContainsEqual = (a: RuleIncExc[], b: RuleIncExc[]): boolean =>
  areSetsEqual(extractHierarchyIds(a), extractHierarchyIds(b));

const extractHierarchyIds = (incExcs: RuleIncExc[]) => {
  const hierarchyIds = new Set<number>();
  for (const { contains } of incExcs) {
    for (const { hierarchyId } of contains) {
      hierarchyIds.add(hierarchyId);
    }
  }
  return hierarchyIds;
};

const areSetsEqual = <T>(a: Set<T>, b: Set<T>) => {
  if (a.size !== b.size) return false;
  for (const itemA of a) {
    if (!b.has(itemA)) return false;
  }
  return true;
};
